import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon, Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Projects", href: "#projects" },
  { label: "Achievements", href: "#achievements" },
  { label: "Skills", href: "#skills" },
  { label: "Contact", href: "#contact" },
];

export const Nav = () => {
  const [dark, setDark] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  return (
    <header
      data-testid="nav-container"
      className="fixed top-5 left-1/2 z-50 w-[92%] max-w-5xl -translate-x-1/2 rounded-full border border-border/50 bg-background/70 px-5 py-3 shadow-lg backdrop-blur-xl"
    >
      <div className="flex items-center justify-between gap-4">
        <a
          data-testid="nav-logo"
          href="#top"
          className="group flex items-center gap-2.5"
          onClick={() => setOpen(false)}
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-terra font-serif text-sm italic text-primary-foreground transition-transform duration-300 group-hover:-rotate-6">
            AG
          </span>
          <span className="hidden font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground sm:block">
            Aditi Gajbhiye
          </span>
        </a>

        <nav className="hidden items-center gap-7 md:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              href={l.href}
              className="text-sm text-muted-foreground transition-colors duration-300 hover:text-terra"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="theme-toggle-button"
            onClick={() => setDark((d) => !d)}
            aria-label="Toggle theme"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-border/60 text-muted-foreground transition-colors duration-300 hover:text-terra"
          >
            {dark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <a
            data-testid="nav-cta-button"
            href="#contact"
            className="group hidden items-center gap-1.5 rounded-full bg-terra px-4 py-2 text-xs font-semibold uppercase tracking-wider text-primary-foreground transition-transform duration-300 hover:scale-[1.04] md:flex"
          >
            Let&apos;s Talk
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <button
            data-testid="nav-menu-button"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-border/60 md:hidden"
          >
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden md:hidden"
          >
            <div className="flex flex-col gap-1 pt-4 pb-1">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="rounded-xl px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-terra"
                >
                  {l.label}
                </a>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
